import React, { useEffect, useState } from "react";
import { useReCaptcha } from "@wss/re-captcha";
import { logException } from "@wss/error-tracking";
import {
  Button,
  Form,
  FormItem,
  InputGroup,
  Input,
} from "@clarkinc/zest-design-system/ui";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import getPriceByEmailRequest from "./request";
import { User, withUserAndChat } from "~/common/Store/connectUserAndChat";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  user: User;
  itemNumber: string;
}

const formSchema = z.object({
  email: z
    .string()
    .nonempty("Email is required.")
    .email("Please enter a valid email address."),
});

const GetPriceByEmail: React.FC<Props> = ({ user, itemNumber, className }) => {
  const defaultSuccessMessage =
    "Thank You! An email has been sent to the address provided.";

  const [errorMessage, setErrorMessage] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const [disabled, setDisabled] = useState(true);

  const recaptcha = useReCaptcha();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: user.emailAddress ?? "" },
  });

  useEffect(() => {
    form.setValue("email", user.emailAddress ?? "");
  }, [user.emailAddress, form]);

  useEffect(() => {
    setDisabled(user.index === undefined);
  }, [user.index]);

  async function onError(errors: FieldErrors<{ email: string }>) {
    if (errors.email?.message) {
      setErrorMessage(errors.email.message);
    }
  }

  const handleSubmit = async ({
    email,
  }: z.infer<typeof formSchema>): Promise<void> => {
    const recaptchaResult = await recaptcha.execute(
      "PriceDiscountEmailSubmission",
    );
    if (recaptchaResult.isError) {
      logException(recaptchaResult.error);
      return;
    }

    setErrorMessage("");
    setSuccessMessage(defaultSuccessMessage);
    getPriceByEmailRequest({
      email,
      itemNumber,
      recaptchaToken: recaptchaResult.value,
      setErrorMessage,
    });
    const { emailSignupEvent } = await import("~/gtm");
    emailSignupEvent(email);
  };

  return (
    <>
      {(errorMessage || !successMessage) && (
        <div className={`price flex ${className}`}>
          <Form
            form={form}
            onSubmit={form.handleSubmit(handleSubmit, onError)}
            aria-label="Get Price By Email"
            theme="wss"
            className="w-3/4 min-w-35 max-w-44"
          >
            <FormItem name="email">
              <InputGroup
                groupLabel="Get Price By Email Input Field"
                inputSize="small"
                theme="wss"
              >
                <Input
                  type="email"
                  label="Email Address"
                  hideError
                  data-testid="email-form-input"
                  disabled={disabled}
                  className="w-full"
                />
                <Button
                  theme="wss"
                  type="submit"
                  color="gray"
                  disabled={disabled}
                  data-testid="email-submit-button"
                >
                  Send
                </Button>
              </InputGroup>
            </FormItem>
          </Form>
        </div>
      )}
      <Message successMessage={successMessage} errorMessage={errorMessage} />
    </>
  );
};

function Message({
  successMessage,
  errorMessage,
}: {
  successMessage: string;
  errorMessage: string;
}) {
  if (errorMessage) {
    return (
      <p
        className="text-xs leading-none mt-2 mb-0 text-red-500 whitespace-normal"
        data-testid="error-message"
      >
        {errorMessage}
      </p>
    );
  }

  if (successMessage) {
    return (
      <p
        role="alert"
        className="text-xs leading-none mt-1-1/4 mb-0 whitespace-normal"
        data-testid="success-message"
      >
        {successMessage}
      </p>
    );
  }

  return null;
}

export default withUserAndChat(GetPriceByEmail);
